import { QuantitySelectorUpdateEvent } from '@theme/events';

export function calculateCarpetArea(length, rollWidth, carpetCount, wastage = 0) {
  if (
    ![length, rollWidth, carpetCount, wastage].every(Number.isFinite) ||
    length <= 0 ||
    rollWidth <= 0 ||
    carpetCount <= 0 ||
    wastage < 0
  ) {
    return null;
  }

  const areaPerCarpet = length * rollWidth;
  const billableAreaPerCarpet = Math.ceil(areaPerCarpet * (1 + wastage / 100) - 1e-9);

  return {
    areaPerCarpet,
    totalArea: areaPerCarpet * carpetCount,
    billableAreaPerCarpet,
    billableArea: billableAreaPerCarpet * carpetCount,
    wastage,
  };
}

if (typeof HTMLElement !== 'undefined' && !customElements.get('teka-carpet-order-calculator')) {
  class TekaCarpetOrderCalculator extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.addEventListener('input', this.handleInput, { signal });
      this.addEventListener('change', this.handleInput, { signal });
      this.closest('.product-details')?.addEventListener('change', this.handleVariantChange, { signal });
      this.calculate();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    handleInput = (event) => {
      if (!event.target.matches('[data-carpet-room-length], [data-carpet-quantity], [data-carpet-wastage]')) return;
      this.calculate();
    };

    handleVariantChange = (event) => {
      const target = event.target;
      const selectedOption = target instanceof HTMLSelectElement ? target.selectedOptions[0] : target;
      if (selectedOption?.dataset.optionName?.toLowerCase() !== 'width') return;

      const rollWidth = Number.parseFloat(selectedOption.value);
      if (!Number.isFinite(rollWidth) || rollWidth <= 0) return;

      this.dataset.rollWidth = String(rollWidth);
      this.calculate();
    };

    calculate() {
      const lengthInput = this.querySelector('[data-carpet-room-length]');
      const carpetQuantityInput = this.querySelector('[data-carpet-quantity]');
      const wastageInput = this.querySelector('[data-carpet-wastage]');
      const length = Number(lengthInput?.value) || 0;
      const carpetCount = Math.max(1, Math.floor(Number(carpetQuantityInput?.value) || 1));
      if (carpetQuantityInput) carpetQuantityInput.value = String(carpetCount);

      const rollWidth = Number(this.dataset.rollWidth) || 0;
      const wastage = wastageInput?.checked ? 10 : 0;
      const price = Number(this.dataset.priceCents) || 0;
      const order = calculateCarpetArea(length, rollWidth, carpetCount, wastage);
      const totalBillableArea = order?.billableArea || 0;
      const productDetails = this.closest('.product-details');
      const productForm = productDetails?.querySelector('product-form-component');
      const quantityInput =
        productForm?.querySelector('quantity-selector-component input[name="quantity"]') ??
        productForm?.querySelector('input[name="quantity"]') ??
        productDetails?.querySelector('input[name="quantity"]');

      this.dataset.units = String(totalBillableArea);
      this.dataset.carpetCount = String(carpetCount);
      this.dataset.roomArea = String(order?.areaPerCarpet || 0);
      this.dataset.roomLength = String(order ? length : 0);
      this.dataset.roomWidth = String(order ? rollWidth : 0);
      this.dataset.billableArea = String(order?.billableAreaPerCarpet || 0);
      this.dataset.wastage = String(wastage);

      const areaOutput = this.querySelector('[data-carpet-room-area]');
      const totalAreaOutput = this.querySelector('[data-carpet-total-area]');
      const totalOutput = this.querySelector('[data-carpet-total]');
      if (areaOutput) areaOutput.textContent = order ? `${order.areaPerCarpet.toFixed(2)} m²` : '— m²';
      if (totalAreaOutput) totalAreaOutput.textContent = order ? `${order.totalArea.toFixed(2)} m²` : '— m²';
      if (totalOutput) {
        totalOutput.textContent = order
          ? new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: this.dataset.currency || 'GBP',
            }).format((totalBillableArea * price) / 100)
          : '—';
      }

      const priceNote = this.querySelector('[data-carpet-price-note]');
      if (priceNote) {
        const checkoutArea = order?.billableAreaPerCarpet;
        priceNote.textContent = order
          ? `${wastage ? 'Includes 10% wastage. ' : ''}${checkoutArea} m² per carpet charged at checkout (rounded up to whole m²).`
          : 'Price is calculated per whole m² for checkout.';
      }

      if (quantityInput) {
        quantityInput.value = order ? String(totalBillableArea) : quantityInput.min || '1';
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        const quantitySelector = quantityInput.closest('quantity-selector-component');
        quantitySelector?.updateButtonStates?.();
        if (quantitySelector) {
          quantitySelector.dispatchEvent(new QuantitySelectorUpdateEvent(totalBillableArea));
        }
      }

      if (this.dataset.productId) {
        const basketButton = productDetails?.querySelector('.add-to-cart-button');
        if (basketButton && !basketButton.disabled) {
          const textNode = [...basketButton.childNodes].find(
            (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim(),
          );
          if (textNode) {
            textNode.textContent = order
              ? `Add ${carpetCount} ${carpetCount === 1 ? 'carpet' : 'carpets'} (${order.totalArea.toFixed(2)} m²) to basket`
              : 'Add to basket';
          }
        }
      }

      const quoteButton = productDetails?.querySelector('[data-room-quote]');
      if (quoteButton) {
        const productTitle = this.dataset.productTitle || '';
        const message = order
          ? `Hi TEKA, please quote ${productTitle}: ${carpetCount} ${carpetCount === 1 ? 'carpet' : 'carpets'}, carpet size ${length} m × ${rollWidth} m (${order.areaPerCarpet.toFixed(2)} m² each; ${order.totalArea.toFixed(2)} m² total)${wastage ? ', including 10% wastage' : ''}.`
          : `Hi TEKA, please quote ${productTitle}.`;
        quoteButton.href = `${this.dataset.whatsappEndpoint}?text=${encodeURIComponent(message)}`;
      }
    }
  }

  customElements.define('teka-carpet-order-calculator', TekaCarpetOrderCalculator);
}
